import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDemo } from "@/contexts/DemoContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  ShoppingBag,
  Shield,
  Store,
  ArrowRight,
  ArrowLeft,
  Rocket,
  CheckCircle2,
} from "lucide-react";

const ONBOARDING_KEY = "chawni-demo-onboarding-seen";

interface Step {
  icon: typeof Sparkles;
  title: string;
  description: string;
  highlight: string;
  action?: { label: string; path: string };
}

const steps: Step[] = [
  {
    icon: Sparkles,
    title: "Welcome to Chawni Phone!",
    description:
      "You've activated Demo Mode with 10,000 MAD demo balance. Explore the platform risk-free — no real money involved.",
    highlight: "Your demo balance expires in 3 days",
  },
  {
    icon: ShoppingBag,
    title: "Browse & Buy Phones",
    description:
      "Explore our catalog of premium smartphones. Add phones to your cart and experience the full checkout flow using your demo balance.",
    highlight: "Try adding a phone to cart now!",
    action: { label: "Browse Shop", path: "/shop" },
  },
  {
    icon: Shield,
    title: "Receive Chawni Passports",
    description:
      "Every phone you buy comes with a unique Chawni Passport — a digital identity card for your device with full ownership history.",
    highlight: "Passports are generated instantly after purchase",
  },
  {
    icon: Store,
    title: "List on the Marketplace",
    description:
      "Got a demo device? List it on the Chawni Marketplace to experience peer-to-peer selling with verified device passports.",
    highlight: "Complete all steps to finish the demo!",
    action: { label: "View Marketplace", path: "/marketplace" },
  },
];

export const DemoOnboardingWizard = () => {
  const { isDemoActive, demoWallet } = useDemo();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (!user || !isDemoActive || !demoWallet) return;
    const seen = localStorage.getItem(`${ONBOARDING_KEY}-${user.id}`);
    if (!seen) {
      const timer = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user, isDemoActive, demoWallet]);

  const handleClose = () => {
    if (user) localStorage.setItem(`${ONBOARDING_KEY}-${user.id}`, "true");
    setOpen(false);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) setCurrentStep((s) => s + 1);
    else handleClose();
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const step = steps[currentStep];
  const StepIcon = step.icon;
  const isLast = currentStep === steps.length - 1;

  const iconColors = [
    "from-emerald-500 to-teal-600",
    "from-blue-500 to-indigo-600",
    "from-violet-500 to-purple-600",
    "from-amber-500 to-orange-600",
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-none bg-transparent shadow-none [&>button]:hidden">
        <div className="rounded-2xl overflow-hidden bg-card border border-border shadow-2xl">
          {/* Progress dots */}
          <div className="flex justify-center gap-2 pt-6 pb-2">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === currentStep
                    ? "w-8 bg-primary"
                    : i < currentStep
                    ? "w-4 bg-primary/40"
                    : "w-4 bg-muted-foreground/20"
                }`}
              />
            ))}
          </div>

          {/* Animated content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="px-8 py-6 text-center"
            >
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div
                  className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${iconColors[currentStep]} flex items-center justify-center shadow-lg`}
                >
                  <StepIcon className="w-10 h-10 text-white" />
                </div>
              </div>

              {/* Step counter */}
              <p className="text-xs font-medium text-muted-foreground mb-2 tracking-wider uppercase">
                Step {currentStep + 1} of {steps.length}
              </p>

              {/* Title */}
              <h3 className="text-xl font-bold text-foreground mb-3 font-display">
                {step.title}
              </h3>

              {/* Description */}
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {step.description}
              </p>

              {/* Highlight pill */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-xs font-medium">
                <Rocket className="w-3.5 h-3.5" />
                {step.highlight}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Actions */}
          <div className="px-8 pb-6 flex items-center gap-3">
            {currentStep > 0 ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-1"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="text-muted-foreground"
              >
                Skip tour
              </Button>
            )}

            <div className="flex-1" />

            {step.action && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  handleClose();
                  navigate(step.action!.path);
                }}
                className="gap-1"
              >
                {step.action.label}
              </Button>
            )}

            <Button size="sm" onClick={handleNext} className="gap-1.5">
              {isLast ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Start Exploring
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
